import {
  browserName,
  isAndroid,
  isDesktop as isDesktopDevice,
  isIOS,
  isMobile as isMobileDevice,
  isTablet as isTabletDevice,
  osName,
} from "react-device-detect";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";

interface DeviceInfo {
  hasPhysicalKeyboard: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  platform: string;
  browser: string;
}

/**
 * Hook to detect device capabilities using react-device-detect
 * Determines if keyboard shortcuts should be enabled based on device type
 */
export function useDeviceDetection(): DeviceInfo {
  // Use react-device-detect for reliable device detection
  const isMobile = isMobileDevice;
  const isTablet = isTabletDevice;
  const isDesktop = isDesktopDevice;
  const isLargeTablet = useMediaQuery("(min-width: 1024px)");

  // Determine if device likely has physical keyboard
  const hasPhysicalKeyboard =
    isDesktop || // All desktop devices have keyboards
    (isTablet && !isIOS && !isAndroid) || // Windows tablets, Surface devices
    (isTablet && isLargeTablet); // Large tablets like iPad Pro

  const deviceInfo = {
    hasPhysicalKeyboard,
    isMobile,
    isTablet,
    isDesktop,
    isAndroid,
    isIOS,
    platform: osName || "Unknown",
    browser: browserName || "Unknown",
  };

  return deviceInfo;
}

/**
 * Simple hook that returns whether keyboard shortcuts should be enabled
 * Excludes mobile devices (iOS/Android phones) but includes tablets and desktops
 */
export function useHasKeyboard(): boolean {
  const { hasPhysicalKeyboard, isMobile, isAndroid, isIOS } =
    useDeviceDetection();

  // Explicitly exclude mobile phones
  if (isMobile && (isAndroid || isIOS)) {
    return false;
  }

  return hasPhysicalKeyboard;
}
